import { captureMessageForSentry } from '@/libraries/sentry';
import { GlobalComponentsPlugin } from '@/plugins/global-components';
import { GlobalDirectivesPlugin } from '@/plugins/global-directives';
import { createVfm } from 'vue-final-modal';

if (process.env.NODE_ENV !== 'production') {
	const originalWarn = console.warn;
	console.warn = (...args) => {
		if (args[0]?.includes?.('Write operation failed')) {
			console.trace('Vue readonly computed write:');
		}
		if (typeof args[0] === 'string' && args[0].includes('[Vue Router warn]')) {
			return;
		}
		originalWarn(...args);
	};
}

interface HydrationMismatchEntry {
	cause: string;
	node: Node | null;
}

declare global {
	interface Window {
		__JW_HYDRATION_MISMATCHES__?: HydrationMismatchEntry[];
		__JW_ON_HYDRATION_MISMATCH__?: (entry: HydrationMismatchEntry) => void;
	}
}

function isThirdPartyDomMutation({ node }: HydrationMismatchEntry): boolean {
	const el = node?.nodeType === Node.ELEMENT_NODE ? (node as Element) : node?.parentElement;
	if (!el) return false;

	// Google Translate / Chrome page translate marks <html> once active.
	// Newer Chrome often replaces text in place (no <font> wrappers), so class markers are the reliable signal.
	const root = document.documentElement;
	if (root.classList.contains('translated-ltr') || root.classList.contains('translated-rtl')) {
		return true;
	}

	// Legacy Google Translate: wraps translated text in <font dir="auto">
	if (el.matches('font[dir="auto"]') || el.querySelector('font[dir="auto"]') != null) return true;

	// Microsoft Edge translator tags translated elements with _msttexthash/_msthash
	const msTranslatorSelector = '[_msttexthash], [_msthash]';
	if (el.closest(msTranslatorSelector) != null || el.querySelector(msTranslatorSelector) != null) {
		return true;
	}

	// Opoint (media monitoring) wraps text nodes in <span opointid opointattr>
	const opointSelector = '[opointid], [opointattr], [opointdata]';
	return el.closest(opointSelector) != null || el.querySelector(opointSelector) != null;
}

/** TikTok in-app WebViews inject DOM before hydration → false children-extra HydrationBails. */
function isInAppWebViewDomMutation(): boolean {
	const ua = navigator.userAgent;
	// Prefer WebView markers over bare "TikTok" so normal browsers with a TikTok referrer are not ignored.
	return /BytedanceWebview|musical_ly|TTWebView/i.test(ua);
}

const NUXT_DEFAULT_ERROR_PAGE_CLASS_MARKERS = ['leading-none', 'tabular-nums', 'font-semibold'] as const;
const NUXT_DEFAULT_ERROR_PAGE_CODES = ['404', '500'] as const;

function elementHasNuxtErrorPageClasses(el: Element): boolean {
	return NUXT_DEFAULT_ERROR_PAGE_CLASS_MARKERS.every(marker => el.classList.contains(marker));
}

function textContainsNuxtErrorPageCode(text: string): boolean {
	return NUXT_DEFAULT_ERROR_PAGE_CODES.some(code => new RegExp(`\\b${code}\\b`).test(text));
}

function isNuxtDefaultErrorPageMismatch(el: any): boolean {
	if (el) {
		let current: Element | null = el;
		while (current) {
			if (textContainsNuxtErrorPageCode(current.textContent ?? '')) {
				if (elementHasNuxtErrorPageClasses(current)) return true;
				if (current.querySelector('h1.leading-none.tabular-nums.font-semibold')) return true;
			}
			current = current.parentElement;
		}
	}

	const bodyText = document.body?.innerText?.slice(0, 1000)?.toLowerCase() ?? '';
	return (
		bodyText.includes('page not found') ||
		bodyText.includes('internal server error') ||
		bodyText.includes('server error')
	);
}

function describeDomNode(node: Node | null): string {
	if (!node) return '[unknown node]';
	const startEl = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
	if (!startEl) return `[detached ${node.nodeName}]`;
	const path: string[] = [];
	let el: Element | null = startEl;
	while (el && el.tagName !== 'HTML' && path.length < 10) {
		let part = el.tagName.toLowerCase();
		if (el.id) part += `#${el.id}`;
		else if (el.classList.length) part += `.${Array.from(el.classList).slice(0, 3).join('.')}`;
		if (el.parentElement) part += `:nth-child(${Array.prototype.indexOf.call(el.parentElement.children, el) + 1})`;
		path.unshift(part);
		el = el.parentElement;
	}
	return path.join(' > ');
}

if (import.meta.client) {
	const HYDRATION_REPORT_LIMIT = 10;
	const NUXT_ERROR_PAGE_REPORT_LIMIT = 10;
	let hydrationReportCount = 0;
	let nuxtErrorPageReportCount = 0;

	function reportHydrationMismatch(entry: HydrationMismatchEntry) {
		if (isThirdPartyDomMutation(entry)) return;
		if (isInAppWebViewDomMutation()) return;

		const el = entry.node?.nodeType === Node.ELEMENT_NODE ? (entry.node as Element) : entry.node?.parentElement;
		const meta = {
			cause: entry.cause,
			selector: describeDomNode(entry.node),
			html: el?.outerHTML?.slice(0, 300) ?? '',
		};

		if (isNuxtDefaultErrorPageMismatch(el)) {
			if (nuxtErrorPageReportCount >= NUXT_ERROR_PAGE_REPORT_LIMIT) return;
			nuxtErrorPageReportCount++;
			captureMessageForSentry(
				'[NuxtDefaultErrorPage]',
				{
					where: '[01.vue-setup.ts]: nuxt default error page mismatch',
					meta,
				},
				'error',
				{
					hydrationMismatchCause: entry.cause,
					nuxtErrorPage: true,
				}
			);
			return;
		}

		if (hydrationReportCount >= HYDRATION_REPORT_LIMIT) return;
		hydrationReportCount++;
		captureMessageForSentry(
			'[HydrationBail]',
			{
				where: '[01.vue-setup.ts]: hydration mismatch hook',
				meta,
			},
			'error',
			{ hydrationMismatchCause: entry.cause }
		);
	}

	(window.__JW_HYDRATION_MISMATCHES__ ?? []).forEach(reportHydrationMismatch);
	window.__JW_ON_HYDRATION_MISMATCH__ = reportHydrationMismatch;
}

export default defineNuxtPlugin({
	name: 'vue-setup',
	parallel: true,
	setup(nuxtApp) {
		nuxtApp.vueApp.config.performance = process.env.NODE_ENV === 'production';
		nuxtApp.vueApp.config.errorHandler = (error, _, info) => {
			// mObs - next steps: sentry may not be initialized yet here, consider a queue system to avoid lost errors
			captureMessageForSentry(
				'[Uncaught Error]:',
				{ where: '[main.ssr.ts]: Vue Error Handler', error: error as Error, meta: { info } },
				'error'
			);
		};

		globalThis.IS_TOUCH = import.meta.client ? !window.matchMedia('(hover: hover)').matches : false;
		nuxtApp.vueApp.use(GlobalComponentsPlugin);
		nuxtApp.vueApp.use(GlobalDirectivesPlugin);
		nuxtApp.vueApp.use(createVfm());

		if (process.env.NODE_ENV !== 'production') {
			import('@/plugins/cls-check').then(module => nuxtApp.vueApp.use(module.ClsCheckPlugin));
		}
	},
});
